import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Save } from "lucide-react";
import { PhaseCard } from "./PhaseCard";
import { emptyPhase, move, uid } from "./roadmapHelpers";

const LIST = (v) => (v || []).join("\n");
const SPLIT = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Field({ label, children }) {
  return <label className="block text-xs text-zinc-400 space-y-1"><span>{label}</span>{children}</label>;
}

function dupPhase(p) {
  return {
    ...p, id: uid(),
    criteria: Object.fromEntries(Object.entries(p.criteria || {}).map(([k, v]) => [k, (v || []).map((c) => ({ ...c, id: uid() }))])),
    content_blocks: (p.content_blocks || []).map((b) => ({ ...b, id: uid(), data: { ...(b.data || {}) } })),
  };
}

export const TemplateEditor = ({ version, readOnly, onSave }) => {
  const [c, setC] = useState(version);
  const [library, setLibrary] = useState([]);
  const [pathways, setPathways] = useState([]);

  useEffect(() => {
    api.get("/exercises", { params: { approved_only: false, limit: 500 } }).then((r) => setLibrary(r.data.items || r.data || []));
    api.get("/rehab-templates/pathways").then((r) => setPathways(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setC({ ...c, [k]: v });
  const phases = c.phases || [];
  const setPhases = (p) => set("phases", p);
  const addExerciseToLibrary = (doc) => setLibrary((lib) => [doc, ...lib.filter((x) => x.id !== doc.id)]);

  const content = () => ({
    title: c.title, patient_description: c.patient_description, intended_population: c.intended_population,
    eligibility: c.eligibility || [], exclusions: c.exclusions || [], condition: c.condition, pathway_id: c.pathway_id || null,
    equipment: c.equipment || [], equipment_alternatives: c.equipment_alternatives,
    phases: phases.map((p, i) => ({ ...p, order: i })),
    references: c.references || [], education: c.education, evidence_references: c.evidence_references || [],
    last_clinical_review: c.last_clinical_review || null, next_review_due: c.next_review_due || null, change_summary: c.change_summary,
  });

  return (
    <form className="space-y-3" data-testid="template-editor" onSubmit={(e) => { e.preventDefault(); onSave(content()); }}>
      {readOnly && <div className="text-xs text-zinc-500">Read-only: v{version.number} is {version.status}. Create a new draft to edit.</div>}
      <div className="grid md:grid-cols-2 gap-2">
        <Field label="Programme name"><input className="fl-input" data-testid="tpl-title" disabled={readOnly} value={c.title || ""} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Clinical condition"><input className="fl-input" disabled={readOnly} value={c.condition || ""} onChange={(e) => set("condition", e.target.value)} /></Field>
        <Field label="Questionnaire pathway (proposed)"><select className="fl-input" data-testid="tpl-pathway" disabled={readOnly} value={c.pathway_id || ""} onChange={(e) => set("pathway_id", e.target.value || null)}><option value="">— none —</option>{pathways.map((p) => <option key={p.id} value={p.id}>{p.label} ({p.id})</option>)}</select></Field>
        <Field label="Intended population"><input className="fl-input" disabled={readOnly} value={c.intended_population || ""} onChange={(e) => set("intended_population", e.target.value)} /></Field>
      </div>
      <Field label="Programme description (patient-friendly)"><textarea className="fl-input" rows={3} data-testid="tpl-description" disabled={readOnly} value={c.patient_description || ""} onChange={(e) => set("patient_description", e.target.value)} /></Field>
      <div className="grid md:grid-cols-3 gap-2">
        <Field label="Eligibility (one per line)"><textarea className="fl-input text-xs" rows={3} disabled={readOnly} value={LIST(c.eligibility)} onChange={(e) => set("eligibility", SPLIT(e.target.value))} /></Field>
        <Field label="Exclusions (one per line)"><textarea className="fl-input text-xs" rows={3} disabled={readOnly} value={LIST(c.exclusions)} onChange={(e) => set("exclusions", SPLIT(e.target.value))} /></Field>
        <Field label="Equipment (one per line)"><textarea className="fl-input text-xs" rows={3} disabled={readOnly} value={LIST(c.equipment)} onChange={(e) => set("equipment", SPLIT(e.target.value))} /></Field>
      </div>
      <Field label="Home / gym alternatives"><textarea className="fl-input text-xs" rows={2} disabled={readOnly} value={c.equipment_alternatives || ""} onChange={(e) => set("equipment_alternatives", e.target.value)} /></Field>

      <div className="space-y-2">
        <div className="text-sm text-zinc-200">Programme Roadmap phases <span className="text-xs text-zinc-500">(ordered; progression is criteria-based)</span></div>
        {phases.map((p, i) => (
          <PhaseCard key={p.id || i} phase={p} idx={i} count={phases.length} library={library} readOnly={readOnly} onLibraryChange={addExerciseToLibrary}
            onChange={(np) => setPhases(phases.map((x, j) => (j === i ? np : x)))}
            onRemove={() => setPhases(phases.filter((_, j) => j !== i))}
            onDuplicate={() => { const next = [...phases]; next.splice(i + 1, 0, dupPhase(p)); setPhases(next); }}
            onMoveUp={() => setPhases(move(phases, i, -1))}
            onMoveDown={() => setPhases(move(phases, i, 1))} />
        ))}
        {!readOnly && <button type="button" className="fl-btn-ghost text-xs" data-testid="add-phase-btn" onClick={() => setPhases([...phases, emptyPhase(phases.length + 1)])}><Plus size={12} /> Add phase</button>}
      </div>

      <Field label="Programme-wide patient education"><textarea className="fl-input text-xs" rows={3} disabled={readOnly} value={c.education || ""} onChange={(e) => set("education", e.target.value)} /></Field>
      <Field label="Evidence references (one per line)"><textarea className="fl-input text-xs" rows={3} data-testid="tpl-evidence" disabled={readOnly} value={LIST(c.evidence_references)} onChange={(e) => set("evidence_references", SPLIT(e.target.value))} /></Field>
      <div className="grid md:grid-cols-3 gap-2">
        <Field label="Last clinical review"><input type="date" className="fl-input" data-testid="tpl-last-review" disabled={readOnly} value={c.last_clinical_review || ""} onChange={(e) => set("last_clinical_review", e.target.value)} /></Field>
        <Field label="Next review due"><input type="date" className="fl-input" data-testid="tpl-next-review" disabled={readOnly} value={c.next_review_due || ""} onChange={(e) => set("next_review_due", e.target.value)} /></Field>
        <Field label="Change summary (readable)"><input className="fl-input" data-testid="tpl-change-summary" disabled={readOnly} value={c.change_summary || ""} onChange={(e) => set("change_summary", e.target.value)} /></Field>
      </div>
      {!readOnly && <button className="fl-btn text-sm" data-testid="save-draft-btn" type="submit"><Save size={14} /> Save draft</button>}
    </form>
  );
};
